import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Search, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import backIcon from '../../../../../Images/addstarsbackIcon.png';
import './activities.css';
import './statuses.css';
import './stages.css';
import Editicon from '../../../../../Images/editstages.png';
import { ChromePicker } from 'react-color';
import { useQuery, useMutation } from '@apollo/client';
import { ADD_UPDATE_ACTIVITY_STATUS, DELETE_ACTIVITY_STATUS, GET_ALL_ACTIVITIES, REORDER_ACTIVITY_STATUSES, UPDATE_ACTIVITY_TOGGLE } from '../../../../../services/userAPI';
import { message } from 'antd';
import PageBreadcrumb from '../../../../../reusableField/PageBreadcrumb';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import BackIcon from '../../../../../Images/backIcon.png';
import HomeIcon from '../../../../../Images/homeIcon.png';

const Activities = () => {
    const [stages, setStages] = useState([]);
    const [originalStages, setOriginalStages] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newStageName, setNewStageName] = useState('');
    const [newStageColor, setNewStageColor] = useState('#000000');
    const [nameError, setNameError] = useState('');
    const [colorError, setColorError] = useState('');
    const [editedStageId, setEditedStageId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editOutcomes, setEditOutcomes] = useState([]);
    const [originalOutcomes, setOriginalOutcomes] = useState([]);
    const [newOutcome, setNewOutcome] = useState('');
    const [editOutcomeValue, setEditOutcomeValue] = useState('');
    const [editingOutcomeIndex, setEditingOutcomeIndex] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [draggedOutcomeIndex, setDraggedOutcomeIndex] = useState(null);
    const [dragOverOutcomeIndex, setDragOverOutcomeIndex] = useState(null);
    const [isSaving, setIsSaving] = useState(false); // New state for tracking saving status

    const location = useLocation();
    const navigate = useNavigate();
    const jobroleId = location.state?.roleId || 22;
    const fullState = location.state || {};


    const { data, loading, error, refetch } = useQuery(GET_ALL_ACTIVITIES, {
        variables: { jobroleId },
        onCompleted: (data) => {
            if (data && data.getAllActivities) {
                const formattedStages = data.getAllActivities.map((activity) => ({
                    id: activity.id,
                    name: activity.activityName,
                    color: activity.status === 1 ? '#0000FF' : '#FF0000',
                    isActive: activity.status === 1,
                    outcomes: activity.activityStatus.map((status) => status.statusName),
                    activityStatus: activity.activityStatus,
                }));
                setStages(formattedStages);
                setOriginalStages([...formattedStages]);
            } else {
                setStages([]);
            }
        },
        onError: (err) => {
            console.error('GraphQL Error:', err.message);
            setStages([]);
            message.error('Failed to load activities. Check console for details.');
        },
    });

    const [updateActivityToggle] = useMutation(UPDATE_ACTIVITY_TOGGLE);
    const [deleteActivityStatus] = useMutation(DELETE_ACTIVITY_STATUS);
    const [addUpdateActivityStatus] = useMutation(ADD_UPDATE_ACTIVITY_STATUS);
    const [reorderActivityStatuses] = useMutation(REORDER_ACTIVITY_STATUSES);

    const [editingId, setEditingId] = useState(null);
    const [editingName, setEditingName] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const inputRef = useRef(null);

    const dartsFormNeeded = location.state?.dartsFormNeeded;
    const accountId = location.state?.accountId;
    const roleId = location.state?.roleId;
    const pocName = location.state?.pocName;
    const pocEmail = location.state?.pocEmail;
    const pocNumber = location.state?.pocNumber;
    const numberLogin = location.state?.numberLogin;
    const emailLogin = location.state?.emailLogin;
    const mandatoryTimeDistribution = location.state?.mandatoryTimeDistribution;
    const targetEditDays = location.state?.targetEditDays;
    const inputEditDays = location.state?.inputEditDays;
    const name = location.state?.name;
    const dartsNeeded = location.state?.dartsNeeded;
    const clanNeeded = location.state?.clanNeeded;
    const syncNeeded = location.state?.syncNeeded;
    const accountName = location.state?.accountName;

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingId, showAddModal, showEditModal]);

    const handleAddStage = () => {
        setShowAddModal(true);
        setEditedStageId(null);
        setNewStageName('');
        setNewStageColor('#000000');
        setNameError('');
        setColorError('');
    };

    const handleEditStage = (id) => {
        const stage = stages.find((s) => s.id === id);
        if (stage) {
            setShowEditModal(true);
            setEditedStageId(id);
            setNewStageName(stage.name);
            setNewStageColor(stage.color);
            const outcomes = stage.activityStatus.map((status) => ({ id: status.id, name: status.statusName }));
            setEditOutcomes(outcomes);
            setOriginalOutcomes([...outcomes]);
            setNameError('');
            setColorError('');
            setNewOutcome('');
            setEditOutcomeValue('');
            setEditingOutcomeIndex(null);
            setDraggedOutcomeIndex(null);
            setDragOverOutcomeIndex(null);
        }
    };

    const handleAddOrUpdateOutcome = async (index = null) => {
        if (isSaving) return; // Prevent multiple clicks during saving
        setIsSaving(true); // Set saving state to true

        if (index !== null && editOutcomeValue.trim()) {
            const outcome = editOutcomes[index];
            try {
                const { data } = await addUpdateActivityStatus({
                    variables: {
                        activityId: editedStageId,
                        activityStatusName: editOutcomeValue.trim(),
                        activityStatusId: outcome.id,
                    },
                });
                if (data.addUpdateActivityStatus.success) {
                    const updatedOutcomes = editOutcomes.map((out, i) =>
                        i === index ? { ...out, name: editOutcomeValue.trim() } : out
                    );
                    setEditOutcomes(updatedOutcomes);
                    setOriginalOutcomes([...updatedOutcomes]);
                    setEditingOutcomeIndex(null);
                    setEditOutcomeValue('');
                    message.success('Outcome updated successfully!');

                    setStages(prevStages =>
                        prevStages.map(stage =>
                            stage.id === editedStageId
                                ? {
                                    ...stage,
                                    outcomes: updatedOutcomes.map(o => o.name),
                                    activityStatus: updatedOutcomes.map(o => ({ id: o.id, statusName: o.name })),
                                }
                                : stage
                        )
                    );
                } else {
                    message.error(data.addUpdateActivityStatus.message || 'Failed to update outcome.');
                }
            } catch (error) {
                message.error('An error occurred while updating the outcome.');
                console.error(error);
            } finally {
                setIsSaving(false); // Reset saving state
            }
        } else if (newOutcome.trim() && index === null) {
            try {
                const { data } = await addUpdateActivityStatus({
                    variables: {
                        activityId: editedStageId,
                        activityStatusName: newOutcome.trim(),
                        activityStatusId: null,
                    },
                });
                if (data.addUpdateActivityStatus.success) {
                    const newId = data.addUpdateActivityStatus.data.id;
                    const newStatus = { id: newId, name: newOutcome.trim() };
                    const updatedEditOutcomes = [...editOutcomes, newStatus];
                    setEditOutcomes(updatedEditOutcomes);
                    setOriginalOutcomes([...updatedEditOutcomes]);
                    setNewOutcome('');
                    message.success('Outcome added successfully!');

                    setStages(prevStages =>
                        prevStages.map(stage =>
                            stage.id === editedStageId
                                ? {
                                    ...stage,
                                    outcomes: [...stage.outcomes, newOutcome.trim()],
                                    activityStatus: [...stage.activityStatus, { id: newId, statusName: newOutcome.trim() }],
                                }
                                : stage
                        )
                    );
                } else {
                    message.error(data.addUpdateActivityStatus.message || 'Failed to add outcome.');
                }
            } catch (error) {
                message.error('An error occurred while adding the outcome.');
                console.error(error);
            } finally {
                setIsSaving(false); // Reset saving state
            }
        } else {
            setIsSaving(false); // Reset if no action taken
        }
    };

    const handleStartEditingOutcome = (index) => {
        setEditOutcomeValue(editOutcomes[index].name);
        setEditingOutcomeIndex(index);
    };

    const handleCancelEditOutcome = (index) => {
        setEditOutcomeValue('');
        setEditingOutcomeIndex(null);
    };

    const handleDeleteOutcome = async (index) => {
        if (isSaving) return; // Prevent multiple clicks during saving
        setIsSaving(true); // Set saving state to true

        const outcome = editOutcomes[index];
        try {
            const { data } = await deleteActivityStatus({
                variables: { activityStatusId: outcome.id },
            });
            if (data.deleteActivityStatus.success) {
                const updatedOutcomes = editOutcomes.filter((_, i) => i !== index);
                setEditOutcomes(updatedOutcomes);
                setOriginalOutcomes([...updatedOutcomes]);
                setEditOutcomeValue('');
                setEditingOutcomeIndex(null);
                message.success('Outcome deleted successfully!');

                setStages(prevStages =>
                    prevStages.map(stage =>
                        stage.id === editedStageId
                            ? {
                                ...stage,
                                outcomes: updatedOutcomes.map(o => o.name),
                                activityStatus: updatedOutcomes.map(o => ({ id: o.id, statusName: o.name })),
                            }
                            : stage
                    )
                );
            } else {
                message.error(data.deleteActivityStatus.message || 'Failed to delete outcome.');
            }
        } catch (error) {
            message.error('An error occurred while deleting the outcome.');
            console.error(error);
        } finally {
            setIsSaving(false); // Reset saving state
        }
    };

    const handleResetOutcomes = () => {
        setEditOutcomes([...originalOutcomes]);
        setEditOutcomeValue('');
        setEditingOutcomeIndex(null);
        setDraggedOutcomeIndex(null);
        setDragOverOutcomeIndex(null);
        message.info('Outcomes reset to original order.');
    };

    const areOutcomesChanged = () => {
        if (editOutcomes.length !== originalOutcomes.length) return true;
        return editOutcomes.some((outcome, index) =>
            outcome.id !== originalOutcomes[index].id ||
            outcome.name !== originalOutcomes[index].name
        );
    };

    const handleReorderOutcomes = async () => {
        if (isSaving) return; // Prevent multiple clicks during saving
        setIsSaving(true); // Set saving state to true

        const updatedActivityStatus = editOutcomes.map(out => ({ id: out.id, statusName: out.name }));
        const updatedOutcomes = editOutcomes.map(out => out.name);
        setStages(prev =>
            prev.map(s =>
                s.id === editedStageId
                    ? { ...s, activityStatus: updatedActivityStatus, outcomes: updatedOutcomes }
                    : s
            )
        );

        try {
            const reorderedIds = editOutcomes.map((outcome) => outcome.id);
            const { data } = await reorderActivityStatuses({
                variables: {
                    activityId: editedStageId,
                    reorderIds: reorderedIds,
                },
            });
            if (data.reorderActivityStatuses.success) {
                message.success('Outcomes reordered successfully!');
                setShowEditModal(false);
                setEditOutcomes([]);
                setOriginalOutcomes([]);
                refetch();
            } else {
                message.error(data.reorderActivityStatuses.message || 'Failed to reorder outcomes.');
            }
        } catch (error) {
            message.error('An error occurred while saving outcomes.');
            console.error(error);
        } finally {
            setIsSaving(false); // Reset saving state
        }
    };

    const handleToggleStage = async (id) => {
        if (isSaving) return; // Prevent multiple clicks during saving
        setIsSaving(true); // Set saving state to true

        const stage = stages.find((s) => s.id === id);
        const newStatus = stage.isActive ? 0 : 1;
        try {
            const { data } = await updateActivityToggle({
                variables: {
                    jobroleId,
                    activityId: id,
                    newActivityStatus: newStatus,
                },
            });
            if (data.updateActivityToggle.success) {
                setStages(
                    stages.map((s) => (s.id === id ? { ...s, isActive: newStatus === 1 } : s))
                );
                message.success('Activity updated successfully!');
                refetch();
            } else {
                message.error(data.updateActivityToggle.message || 'Failed to toggle activity.');
            }
        } catch (error) {
            message.error('An error occurred while toggling activity.');
            console.error(error);
        } finally {
            setIsSaving(false); // Reset saving state
        }
    };

    const handleOutcomeDragStart = (e, index) => {
        setDraggedOutcomeIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleOutcomeDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (draggedOutcomeIndex !== null && draggedOutcomeIndex !== index) {
            setDragOverOutcomeIndex(index);
        }
    };

    const handleOutcomeDragLeave = () => {
        setDragOverOutcomeIndex(null);
    };

    const handleOutcomeDrop = (e, dropIndex) => {
        e.preventDefault();

        if (draggedOutcomeIndex === null || draggedOutcomeIndex === dropIndex) {
            setDraggedOutcomeIndex(null);
            setDragOverOutcomeIndex(null);
            return;
        }

        const newOutcomes = [...editOutcomes];
        const draggedItem = newOutcomes[draggedOutcomeIndex];
        newOutcomes.splice(draggedOutcomeIndex, 1);
        newOutcomes.splice(dropIndex, 0, draggedItem);

        setEditOutcomes(newOutcomes);
        setDraggedOutcomeIndex(null);
        setDragOverOutcomeIndex(null);
    };

    const handleOutcomeDragEnd = () => {
        setDraggedOutcomeIndex(null);
        setDragOverOutcomeIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditingName('');
    };

    const handleBack = () => {
        navigate('/admin');
    };

    const handleBackToViewRole = () => {
        const safeRoleId = roleId || location.state?.roleId;
        const safeAccountName = accountName || location.state?.accountName;

        navigate('/view-role', {
            state: {
                roleId: safeRoleId,
                accountId: accountId || location.state?.accountId,
                accountName: safeAccountName,
                name: name || location.state?.name,
                dartsNeeded: dartsNeeded || location.state?.dartsNeeded,
                clanNeeded: clanNeeded || location.state?.clanNeeded,
                syncNeeded: syncNeeded || location.state?.syncNeeded,
                dartsFormNeeded: dartsFormNeeded || location.state?.dartsFormNeeded,
                pocName: pocName || location.state?.pocName,
                pocEmail: pocEmail || location.state?.pocEmail,
                pocNumber: pocNumber || location.state?.pocNumber,
                numberLogin: numberLogin || location.state?.numberLogin,
                emailLogin: emailLogin || location.state?.emailLogin,
                mandatoryTimeDistribution:
                    mandatoryTimeDistribution || location.state?.mandatoryTimeDistribution,
                targetEditDays: targetEditDays || location.state?.targetEditDays,
                inputEditDays: inputEditDays || location.state?.inputEditDays,
            },
        });
    };

    const handleDiscardClick = () => {
        navigate('/admin');
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setEditOutcomes([]);
        setOriginalOutcomes([]);
        setNameError('');
        setColorError('');
        setNewOutcome('');
        setEditOutcomeValue('');
        setEditingOutcomeIndex(null);
        setDraggedOutcomeIndex(null);
        setDragOverOutcomeIndex(null);
    };

    if (loading) return <div className="loading-container">
        <div className="spinner-loader"></div>
    </div>;
    if (error) return <div>Error: {error.message}</div>;

    const filteredStages = stages.length > 0
        ? stages.filter((stage) => stage.name.toLowerCase().includes(searchTerm.toLowerCase()))
        : [];

    return (
        <div className="user-management-container-stages">
            <div className="mb-4 -mt-[-8px] ">
                <div className="flex items-center justify-between w-full px-2">

                    {/* Back */}
                    <button
                        onClick={() =>
                            navigate("/products-Configuration", {
                                state: fullState,
                            })
                        }
                        className="
                            flex items-center gap-2
                            bg-[#C21A17]
                            text-white
                            px-5 py-2
                            rounded-lg
                            text-sm
                            font-semibold
                            shadow-md
                            hover:bg-[#A91513]
                            transition-all
                            duration-200
                          "
                    >
                        <img
                            src={BackIcon}
                            alt="Back"
                            className="w-[15px] h-[15px] object-contain"
                        />
                        Back
                    </button>

                    {/* Home */}
                    <button
                        onClick={() => navigate("/admin")}
                        className="
                            flex items-center gap-2
                            bg-[#C21A17]
                            text-white
                            px-5 py-2
                            rounded-lg
                            text-sm
                            font-semibold
                            shadow-md
                            hover:bg-[#A91513]
                            transition-all
                            duration-200
                          "
                    >
                        <img
                            src={HomeIcon}
                            alt="Home"
                            className="w-[15px] h-[15px] object-contain"
                        />
                        Home
                    </button>

                </div>
            </div>

            <div>
                <div className="tabs-container-stages">
                    <span
                        className="config-label"
                        onClick={() =>
                            navigate('/productConfiguration', {
                                state: {
                                    name,
                                    accountName,
                                    roleId,
                                    dartsFormNeeded,
                                    dartsNeeded,
                                    roleId,
                                    accountId,
                                    clanNeeded,
                                    syncNeeded,
                                },
                            })
                        }
                    >
                        <span>LDMS Configuration</span>
                    </span>
                    <span className="tabs-stages">
                        <button
                            className="tab-btn"
                            onClick={() =>
                                navigate('/statuses', {
                                    state: {
                                        name,
                                        accountName,
                                        roleId,
                                        dartsFormNeeded,
                                        dartsNeeded,
                                        roleId,
                                        accountId,
                                        clanNeeded,
                                        syncNeeded,
                                    },
                                })
                            }
                        >
                            Statuses
                        </button>
                        <button
                            className="tab-btn"
                            onClick={() =>
                                navigate('/stages', {
                                    state: {
                                        name,
                                        accountName,
                                        roleId,
                                        dartsFormNeeded,
                                        dartsNeeded,
                                        roleId,
                                        accountId,
                                        clanNeeded,
                                        syncNeeded,
                                    },
                                })
                            }
                        >
                            Stages
                        </button>

                        <button
                            className="tab-btn"
                            onClick={() => navigate('/forms-configuration', { state: { name, accountName, roleId, dartsFormNeeded, dartsNeeded, roleId, accountId, clanNeeded, syncNeeded } })}
                        >
                            Forms Configuration
                        </button>
                        <button className="tab-btn active">Activities</button>

                    </span>
                </div>

                <div
                    style={{
                        borderRadius: '4px',
                        marginBottom: '16px',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ position: 'relative', flex: 1 }}>
                        <input
                            type="text"
                            placeholder="Search activity name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                height: '44px',
                                boxSizing: 'border-box',
                                padding: '10px 40px 10px 12px',
                                border: '1px solid #a3a3a3',
                                borderRadius: '8px',
                                fontSize: '14px',
                                outline: 'none',
                            }}
                        />
                        <Search size={14} color="#999" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                    </div>
                </div>

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 16px',
                        backgroundColor: 'rgb(213 212 212)',
                        borderRadius: '4px 4px 0px 0px',
                        border: '1px solid #e8e8e8',
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#333',
                        }}
                    >
                        Activity Name
                    </div>
                    <div
                        style={{
                            flex: 1.2,
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#333',
                        }}
                    >
                        Outcomes
                    </div>
                    <div
                        style={{
                            width: '80px',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#333',
                            textAlign: 'right',
                        }}
                    >
                        Actions
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {filteredStages.length > 0 ? (
                        filteredStages.map((stage, index) => (
                            <div
                                key={stage.id}
                                style={{
                                    backgroundColor: 'white',
                                    padding: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'all 0.2s ease',
                                    transform: dragOverIndex === index ? 'scale(1.02)' : 'scale(1)',
                                    boxShadow: dragOverIndex === index ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                    borderBottom: '1px solid rgb(218 211 211)',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        flex: 1,
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            backgroundColor: stage.color,
                                        }}
                                    />
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            fontFamily: 'Inter',
                                        }}
                                    >
                                        {stage.name}
                                    </span>
                                </div>
                                <div
                                    style={{
                                        flex: 2,
                                        display: 'flex',
                                        gap: '4px',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    {stage.outcomes.length > 0 ? (
                                        stage.outcomes.map((outcome) => (
                                            <span
                                                key={outcome}
                                                style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#ffe6e6',
                                                    borderRadius: '100px',
                                                    fontSize: '12px',
                                                    color: '#333',
                                                    fontFamily: 'Inter',
                                                }}
                                            >
                                                {outcome}
                                            </span>
                                        ))
                                    ) : (
                                        <span style={{ color: '#666' }}>No outcomes defined</span>
                                    )}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px',
                                        width: '80px',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <button
                                        onClick={() => handleEditStage(stage.id)}
                                        style={{
                                            padding: '6px',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <img src={Editicon} alt="Edit" style={{ width: '18px', height: '18px' }} />
                                    </button>
                                    <button
                                        onClick={() => handleToggleStage(stage.id)}
                                        disabled={isSaving}
                                        style={{
                                            padding: '6px',
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {stage.isActive ? (
                                            <ToggleRight color="#AD0101" style={{ margin: '0' }} />
                                        ) : (
                                            <ToggleLeft color="#999" style={{ margin: '0' }} />
                                        )}
                                    </button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                            No activities found.
                        </div>
                    )}
                </div>

                {showEditModal && (
                    <div
                        style={{
                            position: 'fixed',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 1000,
                            backgroundColor: '#F5F5F5',
                            padding: '24px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            width: '600px',
                            maxWidth: '90vw',
                            textAlign: 'left',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3
                                style={{
                                    fontFamily: 'Inter',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#333',
                                }}
                            >
                                Edit Activity
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                disabled={isSaving}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: isSaving ? 'not-allowed' : 'pointer',
                                    padding: '4px',
                                    opacity: isSaving ? 0.7 : 1,
                                }}
                            >
                                <CloseIcon style={{ width: '20px', height: '20px', color: '#333' }} />
                            </button>
                        </div>

                        <div
                            style={{
                                backgroundColor: '#FFFFFF',
                                padding: '16px',
                                borderRadius: '8px',
                                boxShadow:
                                    '0px 2px 2px rgba(0, 0, 0, 0.1), 2px 0px 4px rgba(0, 0, 0, 0.1), -2px 0px 4px rgba(0, 0, 0, 0.1), 0px -2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <div style={{ marginBottom: '20px' }}>
                                <label
                                    style={{
                                        fontFamily: 'Inter',
                                        fontSize: '13px',
                                        fontWeight: '400',
                                        color: '#333',
                                        display: 'block',
                                        marginBottom: '8px',
                                    }}
                                >
                                    Activity Name
                                </label>
                                <input
                                    type="text"
                                    value={newStageName}
                                    readOnly
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        backgroundColor: '#f5f5f5',
                                        color: '#666',
                                        cursor: 'not-allowed',
                                        fontFamily: 'Inter',
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label
                                    style={{
                                        fontFamily: 'Inter',
                                        fontSize: '13px',
                                        fontWeight: '400',
                                        color: '#333',
                                        display: 'block',
                                        marginBottom: '8px',
                                    }}
                                >
                                    Status (Outcome)
                                </label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                    <input
                                        type="text"
                                        value={newOutcome}
                                        placeholder="Enter Status Name"
                                        onChange={(e) => setNewOutcome(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && newOutcome.trim() && handleAddOrUpdateOutcome()}
                                        style={{
                                            flex: 1,
                                            padding: '10px 12px',
                                            border: '1px solid #d9d9d9',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            outline: 'none',
                                            fontFamily: 'Inter',
                                        }}
                                        ref={inputRef}
                                        disabled={isSaving}
                                    />
                                    <button
                                        onClick={() => handleAddOrUpdateOutcome()}
                                        disabled={!newOutcome.trim() || isSaving}
                                        style={{
                                            padding: '10px 24px',
                                            backgroundColor: (newOutcome.trim() && !isSaving) ? '#AD0101' : '#a3a3a3',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: (newOutcome.trim() && !isSaving) ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontFamily: 'Inter',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap',
                                            opacity: (newOutcome.trim() && !isSaving) ? 1 : 0.7,
                                        }}
                                    >
                                        {isSaving ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Add'}
                                    </button>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {editOutcomes.map((outcome, index) => (
                                        <div
                                            key={index}
                                            draggable={!isSaving}
                                            onDragStart={(e) => !isSaving && handleOutcomeDragStart(e, index)}
                                            onDragOver={(e) => !isSaving && handleOutcomeDragOver(e, index)}
                                            onDragLeave={() => !isSaving && handleOutcomeDragLeave()}
                                            onDrop={(e) => !isSaving && handleOutcomeDrop(e, index)}
                                            onDragEnd={() => !isSaving && handleOutcomeDragEnd()}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '8px 12px',
                                                backgroundColor:
                                                    dragOverOutcomeIndex === index ? '#f0f0f0' : '#fafafa',
                                                borderRadius: '4px',
                                                border: '1px solid #f0f0f0',
                                                transition: 'background-color 0.2s ease',
                                                opacity: isSaving ? 0.7 : 1,
                                            }}
                                        >
                                            <DragIndicatorIcon style={{ cursor: isSaving ? 'not-allowed' : 'grab', flexShrink: 0 }} />
                                            {editingOutcomeIndex === index ? (
                                                <input
                                                    type="text"
                                                    value={editOutcomeValue}
                                                    onChange={(e) => setEditOutcomeValue(e.target.value)}
                                                    autoFocus
                                                    style={{
                                                        flex: 1,
                                                        padding: '4px 8px',
                                                        border: '1px solid #d9d9d9',
                                                        borderRadius: '4px',
                                                        fontSize: '14px',
                                                        outline: 'none',
                                                        fontFamily: 'Inter',
                                                    }}
                                                    disabled={isSaving}
                                                />
                                            ) : (
                                                <span
                                                    style={{
                                                        flex: 1,
                                                        fontFamily: 'Inter',
                                                        fontSize: '14px',
                                                        color: '#333',
                                                    }}
                                                >
                                                    {outcome.name}
                                                </span>
                                            )}
                                            {editingOutcomeIndex === index ? (
                                                <>
                                                    <button
                                                        onClick={() => {
                                                            handleAddOrUpdateOutcome(index);
                                                            setEditingOutcomeIndex(null);
                                                        }}
                                                        disabled={isSaving || !editOutcomeValue.trim()}
                                                        style={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            cursor: (isSaving || !editOutcomeValue.trim()) ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: (isSaving || !editOutcomeValue.trim()) ? 0.7 : 1,
                                                        }}
                                                    >
                                                        <CheckIcon
                                                            style={{ width: '18px', height: '18px', color: '#28a745' }}
                                                        />
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCancelEditOutcome(index);
                                                        }}
                                                        disabled={isSaving}
                                                        style={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: isSaving ? 0.7 : 1,
                                                        }}
                                                    >
                                                        <CloseIcon
                                                            style={{ width: '18px', height: '18px', color: '#AD0101' }}
                                                        />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleStartEditingOutcome(index)}
                                                        disabled={isSaving}
                                                        style={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: isSaving ? 0.7 : 1,
                                                        }}
                                                    >
                                                        <img src={Editicon} style={{ width: '16px', height: '16px' }} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteOutcome(index)}
                                                        disabled={isSaving}
                                                        style={{
                                                            padding: '4px',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            cursor: isSaving ? 'not-allowed' : 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            opacity: isSaving ? 0.7 : 1,
                                                        }}
                                                    >
                                                        <Trash2 size={16} color="#AD0101" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '12px',
                                    marginTop: '24px',
                                }}
                            >
                                <button
                                    style={{
                                        padding: '10px 24px',
                                        backgroundColor: 'white',
                                        color: '#333',
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '4px',
                                        cursor: isSaving ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontFamily: 'Inter',
                                        fontWeight: '500',
                                        opacity: isSaving ? 0.7 : 1,
                                    }}
                                    onClick={handleResetOutcomes}
                                    disabled={isSaving}
                                >
                                    Reset
                                </button>
                                <button
                                    onClick={handleReorderOutcomes}
                                    disabled={!areOutcomesChanged() || isSaving}
                                    style={{
                                        padding: '10px 24px',
                                        backgroundColor: (areOutcomesChanged() && !isSaving) ? '#AD0101' : '#a3a3a3',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: (areOutcomesChanged() && !isSaving) ? 'pointer' : 'not-allowed',
                                        fontSize: '14px',
                                        fontFamily: 'Inter',
                                        fontWeight: '500',
                                        opacity: (areOutcomesChanged() && !isSaving) ? 1 : 0.7,
                                    }}
                                >
                                    {isSaving ? <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> : 'Reorder'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {(showAddModal || showEditModal || showDeleteModal) && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 999,
                        }}
                        onClick={handleCloseModal}
                    />
                )}

                <div
                    style={{
                        marginTop: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <a
                        href="#"
                        style={{
                            color: '#1890ff',
                            textDecoration: 'none',
                            fontSize: '14px',
                        }}
                    >
                    </a>
                </div>
            </div>
        </div>
    );
};

export default Activities;